import './AvatarInput.css';
import { useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import imageCompression from 'browser-image-compression';

interface AvatarInputProps {
  initialBase64?: string;
  onChange?: (base64: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const DEFAULT_AVATAR =
  'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQECWAJYAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCACAAIADASIAAhEBAxEB/8QAGwABAAIDAQEAAAAAAAAAAAAAAAYHAwQFCAH/xAAxEAACAgECAggEBwEBAAAAAAAAAQIDBAURBjEHEhMhQWGRoVFxscEiI1JzgZLCMjP/xAAWAQEBAQAAAAAAAAAAAAAAAAAAAQL/xAAWEQEBAQAAAAAAAAAAAAAAAAAAEQH/2gAMAwEAAhEDEQA/AL7ABtkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADV1PNq07T8jMyHtVTBye3N/BLzb2QGvres4Wi43bZ1vV37oVxW85vyX35ECzukbNnY1g4dFNfg7d5yfo0iJ6xqeRq2oW5eXLec33RXKEfCK8kaRrMZqc4HSNmwsSzsOi6vxdW8Je+6J7oes4WtYzuwbet1e6dcu6cH5r78iiTd0bU8jSdQry8SW04Pvi+U4+MX5MQq+waumZtWo6fRmY73qugpLfmvin5p9xtGWgAAAAAAAAAACDdK+XKvSsPFi9lfa5S81Fd3u0Tkr7pcrfYaXZ4KdkX/KT+xcRXAANMgAAs/onypWaVmYsnuqLVKPkpLv90yclfdEVbVGqWeDnXFejf3LBMtYAAigAAAAAAABHOPtMlqXDlyqi5XY7V8Eub233Xo2SMFHncFi8V8CTtvsy9E6n431p40n1e/4wfL+GQjJ0fUsWbjkYGVW18am16ruLWWiDexdH1LKmo4+BlWN/CqSXqyb8KcCTqvhl631PwPrQxovrd/xm+X8IUSDgDTZabw5SrYuN2Q3fNPmt+S9EvUkYBGgAEAAAAAAAAAGHLyacPHsvyrYVU1reU5PZIrfiLj/ACL5Sp0WLx6eXbzW85fJco/X5FRZGVlUYlfXyr6qYfqsmor3ORbxfoVLcXqdT/bUpfRFMZF9uTa7cm2d1j5zsk5P3MZYVddXF+hXPZanWv3FKP1R18XKoy6+vi31XQ+Nc1Jex59MmNfbjWq3GtnTYuU65OL9UIV6EBWPDvH+RRKNOtReRTy7eC2nH5rlL6/MsjEyaczHrvxbYW0zW8Zxe6ZBmABFAAAAAAx5F1ePRZdfNV1VxcpyfJJeJkK76UtZa7LSaJbJpW37eP6Y/f0KiNcW8R3a7mPZyrwa3+VV/qXm/Y4ABUAAVAAADvcI8R36Dmd7lZg2P82r/UfNe5wQRXoTHuryaK7qJqyqyKlCS5NPxMhXfRZrLfa6TfLdJO2jfw/VH7+pYhFAARQAAfO5LdvZLmULrWbLUdWzMuT37a2Ul5LfZL0SLu1q106Pn2rnDHsa/qyg4/8AK+RcTX0AGmQAAAAAAAG7ombLTtWxMuL/APG2Mn5x32a9Ny+ls+9PdeB53fJryL80W13aPgWy5zx62/6ozrWN0AEV/9k=';

const AvatarInput: React.FC<AvatarInputProps> = (props) => {
  const [base64, setBase64] = useState(props.initialBase64 || DEFAULT_AVATAR);

  const processImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.02, //20ko
      maxWidthOrHeight: 256,
      useWebWorker: true,
    }
    try {
      const compressedFile = await imageCompression(file, options);

      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onload = () => {
        setBase64(reader.result as string);
        if (props.onChange) {
          props.onChange(reader.result as string);
        }
      };
    } catch (error) {
      console.error(error);
    }
  };

  const beforeUpload = (file: any) => {
    return processImage(file).then(() => {
      return false;
    });
  };

  const handleChange = (info: any) => {
    if (info.file.status === 'removed') {
      setBase64(DEFAULT_AVATAR);
      if (props.onChange) {
        props.onChange(DEFAULT_AVATAR);
      }
    }
  };

  return (
    <div className="avatar-input">
      <div
        className="avatar-input__preview"
        style={{
          backgroundImage: `url(${base64})`,
        }}
      >
        {base64 != '' && base64 != DEFAULT_AVATAR && !props.disabled ? (
          <div className="avatar-input__preview__delete-action">
            <DeleteOutlined onClick={() => setBase64(DEFAULT_AVATAR)} />
          </div>
        ) : null}
      </div>
      <div className="avatar-input__dragger">
        <Upload.Dragger
          onChange={handleChange}
          beforeUpload={beforeUpload}
          showUploadList={true}
          maxCount={1}
          multiple={false}
          accept="image/*"
          disabled={props.disabled}
          fileList={[]}
          style={{}}
        >
          <p
            className="ant-upload-hint"
            style={{
              margin: 0,
              opacity: props.disabled ? 0.3 : 1,
            }}
          >
            Glissez ici ou sélectionnez
          </p>
        </Upload.Dragger>
      </div>
    </div>
  );
};

export default AvatarInput;
